import { useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Button,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  MuiIcons,
} from '@leap4/frontend';
import { useHistory, useParams } from 'react-router-dom';
import { urlService } from '../../routing/urlsService';
import ReportHeader from '../common/ReportHeader/ReportHeader';
import { useGetConversionUploadBatch } from '../../../api/generated/api';
import type { BatchRecordDto, BatchRowRecordDto } from '../../../api/generated/models';

const { CloudUpload: UploadIcon, ArrowBack: BackIcon, Refresh: RefreshIcon } = MuiIcons;

const POLL_INTERVAL_MS = 4000;

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  return iso.split('T')[0];
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const ordinalSuffix = (day: number): string => {
  const v = day % 100;
  if (v >= 11 && v <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatUploadedDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = `${d.getDate()}${ordinalSuffix(d.getDate())} of ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${date}, ${time}`;
};

const num = (n?: number | null): string => (n === null || n === undefined ? '—' : String(n));

const statusColor = (
  status?: string
): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'completed':
    // A 'created' row appears in the Issues list only because it is an exception
    // (pipeline- or attribution-unmatched) — show why, not the status.
    case 'created':
      return 'success';
    case 'failed':
    case 'error':
    case 'invalid':
      return 'error';
    case 'processing':
    case 'updated':
      return 'info';
    case 'pending':
    case 'duplicate':
    case 'skipped':
      return 'warning';
    default:
      return 'default';
  }
};

const issueChip = (
  row: BatchRowRecordDto
): { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' } => {
  if (row.status === 'created') {
    const attributionUnmatched =
      !!row.metadata && (row.metadata as Record<string, unknown>).attribution_match === false;
    return attributionUnmatched
      ? { label: 'no attribution', color: 'info' }
      : { label: 'no pipeline match', color: 'info' };
  }
  return { label: row.status || '—', color: statusColor(row.status) };
};

interface SummaryCardProps {
  label: string;
  value: string;
  color?: string;
}

const SummaryCard = ({ label, value, color }: SummaryCardProps) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      textAlign: 'center',
      height: '100%',
    }}
  >
    <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
      {value}
    </Typography>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
  </Paper>
);

const BatchDetail = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const query = useGetConversionUploadBatch(id, {
    query: {
      staleTime: 0,
      refetchInterval: (data) => {
        const status = data?.batch?.status;
        return status === 'pending' || status === 'processing' ? POLL_INTERVAL_MS : false;
      },
    },
  });

  const batch: BatchRecordDto | undefined = query.data?.batch;
  const allRows: BatchRowRecordDto[] = useMemo(() => query.data?.rows || [], [query.data]);

  const pagedRows = useMemo(
    () => allRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [allRows, page, rowsPerPage]
  );

  const isPolling = batch?.status === 'pending' || batch?.status === 'processing';

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ReportHeader
        hideTimeNavbar={true}
        breadcrumbs={[
          { link: urlService.conversionUploads(), text: 'Conversion Uploads', icon: <UploadIcon /> },
          { link: '', text: batch?.filename || id },
        ]}
      />
      <Grid container>
        <Grid item xs={12}>
          <Paper sx={{ margin: '10px', padding: '24px', borderRadius: '12px' }} elevation={1}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {batch?.filename || 'Batch'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Campaign: <strong>{batch?.idCampaign || '—'}</strong> · Uploaded{' '}
                  {formatUploadedDate(batch?.uploadedAt)}
                  {batch?.uploadedBy ? ` by ${batch.uploadedBy}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isPolling && <CircularProgress size={18} />}
                <Chip
                  label={batch?.status || '—'}
                  color={statusColor(batch?.status)}
                  sx={{ height: 36, px: 1, fontSize: '0.875rem', fontWeight: 600, textTransform: 'capitalize' }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => query.refetch()}
                  sx={{ textTransform: 'none' }}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BackIcon />}
                  onClick={() => history.push(urlService.conversionUploads())}
                  sx={{ textTransform: 'none' }}
                >
                  Back
                </Button>
              </Box>
            </Box>

            {batch?.errorMessage && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'error.main',
                  color: 'error.main',
                }}
              >
                <Typography variant="body2">{batch.errorMessage}</Typography>
              </Paper>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Row Outcomes
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
              Rows = lines in the file as sent. Repeated in file = a line identical to an earlier one
              (same reference number, date and values), collapsed so it is counted once. Duplicate = a
              row we already hold with nothing changed. Created + Updated + Duplicate + Repeated in
              file sum back to Rows.
            </Typography>
            <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard label="Rows" value={num(batch?.rowCount)} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard
                  label="Repeated in file"
                  value={num(batch?.inFileDuplicateCount)}
                  color="#ed6c02"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard label="Created" value={num(batch?.createdCount)} color="#2e7d32" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard label="Updated" value={num(batch?.updatedCount)} color="#0288d1" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard label="Duplicate" value={num(batch?.duplicateCount)} color="#ed6c02" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard label="Skipped" value={num(batch?.skippedCount)} color="#ed6c02" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard label="Invalid" value={num(batch?.invalidCount)} color="#d32f2f" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard label="Errors" value={num(batch?.errorCount)} color="#d32f2f" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard
                  label="Non-Paid (not attributed)"
                  value={num(query.data?.otherCount)}
                  color="#ed6c02"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Reconciliation
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
              How the uploaded events line up with our own data, across every row this upload created
              or updated. Also in our pipeline = the client-confirmed lead also exists in our organic
              install/purchase data. Client-only = a lead we have no record of, which is stored with no
              IP and so can never match an impression. Attribution = whether the event has matched
              impressions. Rows we already held unchanged are not counted here.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard
                  label="Also in our pipeline"
                  value={num(batch?.pipelineMatchedCount)}
                  color="#2e7d32"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard
                  label="Client-only"
                  value={num(batch?.pipelineUnmatchedCount)}
                  color="#ed6c02"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard
                  label="Attribution matched"
                  value={num(batch?.attributionMatchedCount)}
                  color="#2e7d32"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <SummaryCard
                  label="Attribution unmatched"
                  value={num(batch?.attributionUnmatchedCount)}
                  color="#ed6c02"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Event Date Span
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              {formatDate(batch?.minEventDate)} → {formatDate(batch?.maxEventDate)}
              {batch?.attributionMatchedCount === null && batch?.status === 'completed'
                ? ' · attribution refresh pending'
                : ''}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ margin: '10px', padding: '24px', borderRadius: '12px' }} elevation={1}>
            <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
              Details ({allRows.length})
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Sparse per-row detail: invalid / error / duplicate / skipped, updated (re-upload
              changed revenue / commission / product — see the metadata `changes`), plus created
              rows with no pipeline or attribution match. Fully unchanged successful rows are not
              listed here.
            </Typography>

            {allRows.length === 0 ? (
              <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.6, py: 2 }}>
                {isPolling ? 'Processing…' : 'No exceptions recorded for this batch.'}
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row #</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Lead ID</TableCell>
                        <TableCell>Event Time</TableCell>
                        <TableCell>Message</TableCell>
                        <TableCell>Metadata</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedRows.map((row) => {
                        const chip = issueChip(row);
                        return (
                        <TableRow key={row.id}>
                          <TableCell>{row.rowNumber ?? '—'}</TableCell>
                          <TableCell>
                            <Chip label={chip.label} color={chip.color} size="small" />
                          </TableCell>
                          <TableCell>{row.leadId || '—'}</TableCell>
                          <TableCell>{formatDateTime(row.eventTimestamp)}</TableCell>
                          <TableCell>{row.errorMessage || '—'}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 280,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {row.metadata ? (
                              <Typography
                                variant="caption"
                                component="span"
                                title={JSON.stringify(row.metadata)}
                              >
                                {JSON.stringify(row.metadata)}
                              </Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={allRows.length}
                  page={page}
                  onPageChange={(_e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default BatchDetail;
